<template>
  <div>
    <NuxtLayout
      name="asuostaticlayout"
      :title="$page?.studentsCourses?.title"
      :image-source="$page?.studentsCourses?.heroImage"
    >
      <!-- section-introduction -->
      <section
        class="py-space-lg py-lg-space-xxl bg-light-2"
        :style="getBgImage($page?.studentsCourses?.introBackground!)"
      >
        <div class="container px-space-sm px-md-0">
          <div class="row">
            <div
              class="col-12 offset-lg-1 col-lg-10 d-flex flex-column flex-lg-row p-space-md pt-md-space-xl ps-md-space-xl bg-white"
            >
              <div>
                <h2 class="h2-medium mb-space-sm">
                  {{ $page?.studentsCourses?.introTitle }}
                </h2>
                <div
                  class="fs-medium mb-space-sm"
                  v-html="$page?.studentsCourses?.introDescription"
                ></div>
                <div
                  class="fs-medium mb-space-md fw-bold"
                  v-html="$page?.studentsCourses?.introSecondary"
                ></div>

                <div class="mb-space-xs pe-md-space-xs">
                  <div
                    v-for="(eachItem, index) in $page?.studentsCourses?.introStats"
                    :key="index"
                    class="mb-space-xs d-flex flex-row"
                  >
                    <h3
                      class="h3-large bg-secondary py-lg-space-xxs py-0 px-space-xs me-space-xs d-inline-block align-self-center"
                    >
                      {{ eachItem?.title }}
                    </h3>
                    <p class="fw-bold mb-0 fs-xl wrap-text align-self-center">
                      {{ eachItem?.text }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="d-none d-lg-block">
                <img
                  :alt="$page?.studentsCourses?.introImageAlt!"
                  class="ms-space-lg"
                  :src="$page?.studentsCourses?.introImage!"
                  height="470"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- video-section -->
      <SectionVideoModal
        v-if="$page?.studentsCourses?.videoSection"
        :title="$page?.studentsCourses?.videoSection.title!"
        :video-source="videoEmbedUrl($page?.studentsCourses?.videoSection.youtubeId!)"
        :display-play-button="true"
        :bg-image-source="$page?.studentsCourses?.videoSection.imageSource!"
        @modal-open="
          handleModalEvent('open', $page?.studentsCourses?.videoSection!.title!, 'play button')
        "
        @modal-close="
          handleModalEvent('close', $page?.studentsCourses?.videoSection!.title!, 'close cross')
        "
      ></SectionVideoModal>

      <!-- accordion section -->
      <section class="py-0 py-md-space-md py-lg-space-lg bg-light-3">
        <div class="container px-space-xs px-lg-space-lg py-space-lg py-lg-space-lg">
          <div class="row gx-0 flex-column">
            <h2
              class="mb-space-sm px-0 col-12 col-lg-8 w-auto h2-medium text-dark-3 align-self-start"
            >
              {{ $page?.studentsCourses?.accordionTitle }}
            </h2>
            <div class="col-12">
              <div class="courses-title-underline mb-space-md bg-secondary align-self-start"></div>
            </div>
          </div>
          <overlap-accordion-atlas
            default-accordion-image="getDefaultAccordionImage"
            :accordion-items="$page?.studentsCourses?.accordionItems! as unknown as Item[]"
            @collapse-shown="
              handleCollapseEvent('open', $page?.studentsCourses?.accordionTitle!, $event.title!)
            "
            @collapse-hidden="
              handleCollapseEvent('close', $page?.studentsCourses?.accordionTitle!, $event.title!)
            "
          />
        </div>
      </section>

      <!-- video-card -->
      <section class="bg-white py-space-lg py-lg-space-xxl">
        <div class="container">
          <div class="row">
            <div class="col-12">
              <VideoModalAtlas
                :video-source="videoEmbedUrl($page?.studentsCourses?.videoCard?.youtubeId!)"
                :bg-image-source="$page?.studentsCourses?.videoCard?.imageSource!"
                :title="$page?.studentsCourses?.videoCard?.title!"
                video-position="right"
                @modal-open="
                  handleModalEvent(
                    'open',
                    $page?.studentsCourses?.videoCard?.title!,
                    'video play button'
                  )
                "
                @modal-close="
                  handleModalEvent(
                    'close',
                    $page?.studentsCourses?.videoCard?.title!,
                    'close cross'
                  )
                "
              >
                <div class="mb-0" v-html="$page?.studentsCourses?.videoCard?.description!"></div>
              </VideoModalAtlas>
            </div>
          </div>
        </div>
      </section>

      <!-- schedule-section -->
      <section class="your-schedule py-space-lg pt-lg-0 pb-lg-space-xxl bg-dark-3">
        <div class="container">
          <div class="row gx-0">
            <div class="col-12">
              <div class="mb-space-md mb-lg-space-xl">
                <img
                  class="your-schedule-hero-img"
                  :src="$page?.studentsCourses?.scheduleImage!"
                  :alt="$page?.studentsCourses?.scheduleImageAlt!"
                />
              </div>

              <h2 class="h2-medium mb-space-sm text-white">
                {{ $page?.studentsCourses?.scheduleTitle }}
              </h2>

              <div
                class="mb-space-lg text-white ps-0 col-10"
                v-html="$page?.studentsCourses?.scheduleDescription"
              ></div>
            </div>
          </div>

          <div class="row">
            <div
              v-for="eachCard in $page?.studentsCourses?.scheduleItems"
              :key="eachCard?.internalId!"
              class="col-12 col-lg-4 px-space-xs mb-space-lg mb-lg-0"
            >
              <img
                class="mb-space-sm px-space-sm px-lg-0"
                height="255"
                :src="eachCard?.icon!"
                :alt="eachCard?.iconAlt!"
              />
              <h3 class="h3-large text-secondary mb-space-xs">
                {{ eachCard?.title }}
              </h3>
              <div class="text-white mb-0 fs-small" v-html="eachCard?.description"></div>
            </div>
          </div>
        </div>
      </section>

      <!-- section-learning -->
      <section
        class="slide-in-section container py-space-lg py-lg-space-xl"
        aria-labelledby="learning-section-title"
      >
        <ContentSlideInAtlas
          content-box-class="position-relative py-lg-space-md px-lg-space-sm"
          :image-source="$page?.studentsCourses?.learningImage!"
          card-position="left"
        >
          <template #card-content>
            <h3 id="learning-section-title" class="h3-xl text-dark-3 mb-space-sm" tabindex="0">
              {{ $page?.studentsCourses?.learningTitle }}
            </h3>
            <div
              class="fs-large text-dark-3 mb-space-xs"
              tabindex="0"
              v-html="$page?.studentsCourses?.learningDescription"
            ></div>
          </template>
        </ContentSlideInAtlas>
      </section>

      <!-- section-classes -->
      <SectionStepApollo
        :image-source="$page?.studentsCourses?.classesImage!"
        :display-step="false"
        :title="$page?.studentsCourses?.classesTitle!"
      >
        <div class="mb-space-md" v-html="$page?.studentsCourses?.classesBody"></div>
        <NuxtLink
          :href="$page?.studentsCourses?.classesLink?.uri!"
          :target="$page?.studentsCourses?.classesLink?.target"
          role="button"
          class="class-section-link rds-link btn btn-secondary fw-bold fs-small py-space-xxs px-space-xs"
          @click="
            handleCtaClick(
              $page?.studentsCourses?.classesTitle!,
              $page?.studentsCourses?.classesLink! as unknown as ParagraphHtmlLink,
              $page?.studentsCourses?.classesTitle!
            )
          "
        >
          {{ $page?.studentsCourses?.classesLink?.text }}
        </NuxtLink>
      </SectionStepApollo>

      <!-- FAQ section -->
      <section class="px-0 bg-light-3 py-0 py-lg-space-xxl">
        <div class="container">
          <div class="row">
            <div
              class="offset-lg-1 faq-container col-12 col-lg-10 py-space-lg py-lg-space-xxl px-space-xs px-md-space-md px-lg-space-xxl"
            >
              <h2 class="h2-medium mb-space-sm">
                {{ $page?.studentsCourses?.faqTitle }}
              </h2>
              <div class="courses-title-underline mb-space-md bg-secondary align-self-start"></div>
              <RdsAccordion>
                <collapse-item
                  v-for="(eachFaqItem, i) in $page?.studentsCourses?.faqItems"
                  :key="generateCollapseKey(i)"
                  :collapse-id="generateCollapseKey(i)"
                  :display-number="false"
                  :title="eachFaqItem?.title!"
                  title-size="medium"
                  class="mb-space-xs mb-md-space-sm bg-white"
                  border-variant="light-4"
                  accordion-group-id="rankings-second-accordion"
                  @collapse-shown="
                    handleCollapseEvent(
                      'open',
                      $page?.studentsCourses?.faqTitle!,
                      eachFaqItem?.title!
                    )
                  "
                  @collapse-hidden="
                    handleCollapseEvent(
                      'close',
                      $page?.studentsCourses?.faqTitle!,
                      eachFaqItem?.title!
                    )
                  "
                >
                  <div v-html="eachFaqItem?.description"></div>
                </collapse-item>
              </RdsAccordion>
            </div>
          </div>
        </div>
      </section>

      <!-- section-explore-your-future -->
      <SectionExploreYourFuture
        :title="$page?.studentsCourses?.exploreTitle!"
        :background="$page?.studentsCourses?.exploreBg!"
        :items="$page?.studentsCourses?.exploreCards! as ExploreItem[]"
      ></SectionExploreYourFuture>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import type { Item } from "@rds-vue-ui/overlap-accordion-atlas";
import { OverlapAccordionAtlas } from "@rds-vue-ui/overlap-accordion-atlas";
import { RdsAccordion } from "@rds-vue-ui/rds-accordion";
import { SectionStepApollo } from "@rds-vue-ui/section-step-apollo";
import { SectionVideoModal } from "@rds-vue-ui/section-video-modal";
import { VideoModalAtlas } from "@rds-vue-ui/video-modal-atlas";
import type { ExploreItem } from "~/components/students/SectionExploreYourFuture.vue";
import SectionExploreYourFuture from "~/components/students/SectionExploreYourFuture.vue";
import type { ParagraphHtmlLink, StudentsCourses } from "~/server/resolvers/resolvers-types";
import type { StudentsCoursesQuery } from "~/types/graphql-operations";

const getDefaultAccordionImage = ref("");

const UtilityService = useUtilityService();

definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"]
  }
});

const config = useRuntimeConfig();

const $page = await usePageQuery<StudentsCoursesQuery>(
  /* GraphQL */ `
    #graphql
    query studentsCourses($studentsCoursesId: ID!) {
      studentsCourses(id: $studentsCoursesId) {
        detailPage
        metaTitle
        metaDescription
        heroImage
        title
        introTitle
        introDescription
        introSecondary
        introImage
        introImageAlt
        introBackground
        introStats {
          title
          text
        }
        videoSection {
          title
          description
          youtubeId
          imageSource
          imageAlt
          uploadDate
          duration
          captionText
        }
        accordionTitle
        accordionItems {
          id
          accordionId
          title
          body
          image
        }
        videoCard {
          title
          description
          youtubeId
          imageSource
          imageAlt
          uploadDate
          duration
          captionText
        }
        scheduleTitle
        scheduleDescription
        scheduleImage
        scheduleImageAlt
        scheduleItems {
          internalId
          title
          description
          icon
          iconAlt
        }
        learningTitle
        learningDescription
        learningImage
        classesTitle
        classesImage
        classesBody
        classesLink {
          text
          uri
          target
        }
        faqTitle
        faqItems {
          internalId
          title
          description
        }
        exploreTitle
        exploreBg
        exploreCards {
          title
          image
          imageAlt
          link {
            text
            uri
            target
          }
        }
        created
        changed
      }
    }
  `,
  {
    studentsCoursesId: "808fda17-bcc9-40c9-8eb9-80a10a051425"
  },
  {
    key: "students-corses"
  }
);

useHead({
  title: $page?.studentsCourses?.metaTitle,
  meta: [
    { name: "Title", content: $page?.studentsCourses?.metaTitle },
    {
      name: "Description",
      content: $page?.studentsCourses?.metaDescription
    }
  ],
  link: [
    {
      rel: "canonical",
      href: UtilityService.formatCanonicalUrl(
        `${config.public.siteUrl}${$page?.studentsCourses?.detailPage}`
      )
    }
  ],
  script: [
    useSchemaService().generateStudentsCourseSchema(
      $page?.studentsCourses as unknown as StudentsCourses
    )
  ]
});

const generateCollapseKey = (index: number) => {
  return "faqItem" + index;
};

const videoEmbedUrl = (url: string) => {
  return `https://www.youtube.com/embed/${url}`;
};

const getBgImage = (img: string) => {
  return { "background-image": `url('${img}')` };
};

const handleCtaClick = (title: string, link: ParagraphHtmlLink, component: string) => {
  if (link?.text) {
    analyticsComposable.trackLinkEvent(
      "internal link",
      "main content",
      title.toLowerCase(),
      link.text.toLowerCase(),
      component.toLowerCase()
    );
  } else {
    console.error("missing link text");
  }
};

const handleCollapseEvent = (action: string, section: string, text: string) => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    action.toLowerCase(),
    "click",
    "main content",
    section.toLowerCase(),
    text.toLocaleLowerCase(),
    "accordion-standard"
  );
};

const handleModalEvent = (action: string, section: string, text: string) => {
  analyticsComposable.trackModalEvent(
    "onclick",
    action,
    "click",
    "main content",
    section.toLowerCase(),
    text
  );
};

watchEffect(() => {
  const accordionItems = $page?.studentsCourses?.accordionItems;
  if (accordionItems && accordionItems.length > 0) {
    getDefaultAccordionImage.value = accordionItems[0]?.image ?? "";
  }
});
</script>

<style lang="scss" scoped>
.separator-underline {
  width: 56px;
  height: 8px;
}

.your-schedule {
  background: var(--rds-dark-3);
  background: linear-gradient(
    #fff 0%,
    #fff 25%,
    var(--rds-dark-3, #191919) 25%,
    var(--rds-dark-3, #191919) 100%
  );
}

.your-schedule-hero-img {
  width: 100%;
  height: auto;
}

.slide-in-section :deep(.rds-slidein-atlas-container .slide-in-left) {
  animation-delay: 100ms;
  animation-fill-mode: both;
}

.slide-in-section :deep(.rds-slidein-atlas-container .scale-back) {
  animation-delay: 50ms;
  animation-fill-mode: both;
  animation-timing-function: ease-in-out;
}

.class-section-link {
  text-decoration: none;
}

.courses-title-underline {
  height: 8px;
  width: 64px;
}

.faq-container {
  background: var(--rds-white);
}

@media (width <= 992px) {
  .your-schedule-hero-img {
    display: none;
  }

  .your-schedule {
    background: var(--rds-dark-3);
  }

  .faq-container {
    background: transparent;
  }
}
</style>
